import {
  Actions,
  Async,
  Dispatchers,
  Option,
  SMap,
  TMap,
  toAsyncFetched,
} from '@client/new/helpers/types';
import {
  Submission,
  SubmissionMedia,
  UserStatus,
} from '../model/Challenges.types';
import { useDeferredValue, useState } from 'react';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useTabHash } from '@client/new/hooks/useTabHash';
import {
  useChallengeDetailsModel,
  UseChallengeDetailsParams,
} from '../model/ChallengeDetails.model';
import {
  SubmissionCardMode,
  SubmissionCardVM,
  SubmissionVM,
  toSubmissionCardVM,
  toSubmissionVM,
} from './Submission.viewModel';
import { Breadcrumb } from '../view/components/common/Breadcrumbs';
import { exportCSV, toBreadcrumbs } from './ChallengeDetails.mapper';
import { PATHS } from '@client/routes';
import { keys } from '@client/new/helpers/general';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { downloadZIP } from '@client/new/helpers/files';

type ChallengeFilter = Option<Filter> & { checked: boolean; selected?: string };

export type ChallengeDetailsVM = {
  selectedSubmission: SubmissionVM | null;
  breadcrumbs: Array<Breadcrumb>;
  programName: string;
  title: string;
  backURL: string;
  totalSubs: number;
  newSubs: number;
  submissions: SubmissionCardVM[];
  totalSubmissions: number;
  createOpen: boolean;
  activeTab: SubmissionsTab;
  filters: Array<ChallengeFilter>;
  searchText: string;
} & Dispatchers<ChallengeVMActionMap>;

const basicFilters = [
  'Completed',
  'Instagram',
  'Testimonials',
  'Video',
  'Repostable',
] as const;
const allFilters = [...basicFilters, 'UserStatus'] as const;

export type Filter = (typeof allFilters)[number];

const matchesSearch = (searchText: string, s: Submission): boolean => {
  const tokens = searchText.toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = `${s.username} ${s.email}`.toLocaleLowerCase();
  return tokens.every((token) => haystack.includes(token));
};

const isActive = (
  filtersMap: Partial<TMap<Filter, boolean | string | null>>,
  s: Submission,
) => {
  const activeFilters = keys(filtersMap).filter((f) =>
    Boolean(filtersMap[f as Filter]),
  );

  return activeFilters.every((k) => {
    switch (k as Filter) {
      case 'Completed':
        return !!s.afterDate;
      case 'Instagram':
        return s.instagram !== '';
      case 'Testimonials':
        return s.testimonial !== '';
      case 'Video':
        return (
          s?.beforeMedia?.some((m) => m.type === 'video') ||
          s?.afterMedia?.some((m) => m.type === 'video')
        );
      case 'Repostable':
        return s.repostable;
      case 'UserStatus':
        return s.userStatus === filtersMap[k];
    }
  });
};

type ChallengeVMActionMap = {
  ToggleFilter: Filter;
  SetUserStatusFilter: UserStatus | null | undefined;
  SetSearchText: string;
  SetSubmission: string;
  CloseModal: null;
  SetTab: ChallengeDetailsVM['activeTab'];
  CreateNew: null;
  ToggleFav: string;
  ExportCSV: null;
  DownloadZIP: null;
};

type ChallengeVMAction = Actions<ChallengeVMActionMap>;

const tabKeys = ['all', 'shortlisted'] as const;
const toZipable = (v: SubmissionMedia, i: number) => ({
  url: v.url,
  name:
    i +
    '.' +
    (v.url.replace(/.*\./g, '') || (v.type === 'video' ? 'MP4' : 'JPG')),
});

export type SubmissionsTab = (typeof tabKeys)[number];
export const useChallengeDetailsVM = (
  p: UseChallengeDetailsParams,
): Async<ChallengeDetailsVM> => {
  const { tabHash, setTabHash } = useTabHash(tabKeys, 'all');
  const [favLoaders, setFavLoaders] = useState<SMap<boolean>>({});
  const model = useChallengeDetailsModel(p);
  const [searchText, setSearchText] = useState('');
  const deferredSearchText = useDeferredValue(searchText);
  const [filtersMap, setFilterMap] = useState<
    Partial<TMap<Filter, boolean | string | null>>
  >({});
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<
    string | null
  >(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [submissionModalMode, setSubmissionModalMode] =
    useState<SubmissionCardMode>('Before/after');

  if (model.type !== 'fetched') return model;

  const download = () => {
    const s = model.value.submissions.find(
      (s) => s.submissionId === selectedSubmissionId,
    );
    if (!s) return;

    const files = [
      ...s.beforeMedia,
      ...s.afterMedia,
      ...s.transformationMedia,
    ].map(toZipable);

    return downloadZIP(files, `${s.username}_${Date.now()}.zip`);
  };

  const _dispatch = (a: ChallengeVMAction) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'ToggleFilter':
        setFilterMap((prev) => ({
          ...prev,
          [a.payload]: !prev[a.payload],
        }));
        return;

      case 'SetUserStatusFilter':
        return setFilterMap((prev) => ({
          ...prev,
          UserStatus: a.payload,
        }));
      case 'SetSearchText':
        return setSearchText(a.payload);
      case 'SetSubmission':
        setSelectedSubmissionId(
          model.value.submissions.find((s) => s.submissionId === a.payload)
            ? a.payload
            : null,
        );
        setSubmissionModalMode('Before/after');
        return;
      case 'CloseModal':
        return setSelectedSubmissionId(null);
      case 'SetTab':
        setTabHash(a.payload);
        return;
      case 'CreateNew':
        setCreateOpen(!createOpen);
        return;

      case 'ExportCSV':
        exportCSV(model.value.submissions, model.value.name);
        return;

      case 'DownloadZIP':
        download();
        return;

      case 'ToggleFav': {
        if (favLoaders[a.payload]) return;
        const s = model.value.submissions.find(
          (s) => s.submissionId === a.payload,
        );
        if (!s) return;
        setFavLoaders((prev) => ({ ...prev, [a.payload]: true }));
        model.value
          .toggleFav({
            submissionId: s.submissionId,
            isFav: !s.isFav,
          })
          .finally(() =>
            setFavLoaders((prev) => ({ ...prev, [a.payload]: false })),
          );
        return;
      }
    }
  };

  const dispatchers = getDispatchers<ChallengeVMActionMap>(_dispatch);

  const filters = allFilters.map(
    (f): ChallengeFilter => ({
      value: f,
      checked: Boolean(filtersMap[f]),
      label: f,
      selected: typeof filtersMap[f] === 'string' ? filtersMap[f] : undefined,
    }),
  );

  const submissions = (
    tabHash === 'all'
      ? model.value.submissions
      : model.value.submissions.filter((s) => s.isFav)
  ).filter((s) => matchesSearch(deferredSearchText, s));

  const filteredSubmissions = (
    Object.values(filtersMap).every((v) => v === false)
      ? submissions
      : submissions.filter((s) => isActive(filtersMap, s))
  )
    .map((s) =>
      toSubmissionCardVM(s, (id) => dispatchers.dispatch('SetSubmission', id)),
    )
    .map((s) => ({ ...s, isFavLoading: Boolean(favLoaders[s.id]) }));
  const submission = model.value.submissions.find(
    (s) => s.submissionId === selectedSubmissionId,
  );

  return toAsyncFetched<ChallengeDetailsVM>({
    title: model.value.name,
    breadcrumbs: toBreadcrumbs(model.value),
    createOpen,
    totalSubs: model.value.submissions.length,
    newSubs: model.value.submissions.length,
    activeTab: tabHash,
    totalSubmissions: model.value.submissions.length,
    filters,
    searchText,
    selectedSubmission: submission
      ? {
          ...toSubmissionVM(submission),
          isFavLoading: Boolean(favLoaders[submission.submissionId]),
          mode: submissionModalMode,
          changeMode: setSubmissionModalMode,
        }
      : null,
    submissions: filteredSubmissions,
    programName: model.value.programName,

    backURL: PATHS.PROGRAM_EDIT(
      model.value.programId,
      model.value.creatorId,
      'challenges',
    ),
    ...dispatchers,
  });
};
